function apiBaseUrl() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';
}

/** Maps draft / composer channel strings to BE integrate-asset `channel` enum. */
export function normalizeIntegrateAssetChannel(raw) {
  const c = String(raw || '').toLowerCase().trim();
  if (c === 'call') return 'call';
  if (c === 'linkedin' || c === 'linkedin_dm' || c === 'linkedin_connect') return 'linkedin';
  return 'email';
}

/**
 * POST /api/personas/:person_id/integrate-asset — weaves asset above CTA (HTML or plain text per channel).
 * @returns {{ ok: boolean, data: { body_html?: string, integrated?: boolean, reason?: string } | null }}
 */
export async function integratePersonaAsset({
  personId,
  tenantId,
  userId,
  bodyHtml,
  asset,
  recipientFirstName,
  companyName,
  language = 'English',
  channel = 'email',
}) {
  const pid = String(personId ?? '').trim();
  if (!pid) return { ok: false, data: null };

  const rawType = asset?.type;
  const type =
    rawType === 'case_study' || rawType === 'client_reference'
      ? 'case_study'
      : rawType === 'product_link'
        ? 'product_link'
        : 'product_link';
  const integrateChannel = normalizeIntegrateAssetChannel(channel);

  const headers = {
    'Content-Type': 'application/json',
    'x-tenant-id': String(tenantId ?? ''),
  };
  if (userId != null && String(userId).trim() !== '') {
    headers['x-user-id'] = String(userId).trim();
  }

  const res = await fetch(`${apiBaseUrl()}/api/personas/${encodeURIComponent(pid)}/integrate-asset`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      body_html: String(bodyHtml ?? ''),
      asset: {
        type,
        name: String(asset?.name || '').trim() || 'Link',
        url: String(asset?.url || '').trim(),
      },
      recipient_first_name: recipientFirstName || undefined,
      company_name: companyName || undefined,
      language: language || 'English',
      channel: integrateChannel,
    }),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  return { ok: res.ok, data };
}

/**
 * Integrate each asset in order into the same draft body (shape depends on channel: HTML vs plain text).
 * Skips `asset_already_present` without changing body. Stops on first other failure.
 * @param {string} [channel] — raw composer channel, e.g. `email`, `linkedin_dm`, `call` (normalized for BE).
 * @returns {{ bodyHtml: string, stopReason: string | null }}
 */
export async function integrateSequentiallyIntoBody({
  personId,
  tenantId,
  userId,
  initialHtml,
  assets,
  recipientFirstName,
  companyName,
  language,
  channel = 'email',
}) {
  let out = String(initialHtml ?? '');
  const list = Array.isArray(assets) ? assets : [];
  for (const asset of list) {
    if (!asset?.url && asset?.type !== 'client_reference') continue;
    const { ok, data } = await integratePersonaAsset({
      personId,
      tenantId,
      userId,
      bodyHtml: out,
      asset,
      recipientFirstName,
      companyName,
      language,
      channel,
    });
    if (ok && data?.integrated && typeof data.body_html === 'string') {
      out = data.body_html;
      continue;
    }
    if (data?.reason === 'asset_already_present') continue;
    const reason = data?.reason || (!ok ? 'request_failed' : 'not_integrated');
    return { bodyHtml: out, stopReason: reason };
  }
  return { bodyHtml: out, stopReason: null };
}
